import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  Chip,
  Grid,
  Container,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warehouse as WarehouseIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error'; // แก้ไข import
import { ViewTitle } from '../../components/ViewTitle'; // แก้ไข path

// Utility functions
const formatNumber = (value, decimals = 2) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

const parseNumber = (value) => {
  if (!value || value === '') return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

// Editable field component
const EditableField = ({
  value,
  onSave,
  isInteger = false,
  readOnly = false,
}) => {
  const [displayValue, setDisplayValue] = useState(
    formatNumber(value, isInteger ? 0 : 2),
  );
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setDisplayValue(formatNumber(value, isInteger ? 0 : 2));
  }, [value, isInteger]);

  const handleChange = (e) => {
    if (readOnly) return;
    const inputValue = e.target.value;
    if (inputValue === '' || /^[\d,]*\.?\d*$/.test(inputValue)) {
      setDisplayValue(inputValue);
    }
  };

  const handleBlur = () => {
    if (readOnly) return;
    const numValue = parseNumber(displayValue);
    setDisplayValue(formatNumber(numValue, isInteger ? 0 : 2));
    setIsEditing(false);
    if (numValue !== value) {
      onSave(numValue);
    }
  };

  return (
    <TextField
      size="small"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={() => !readOnly && setIsEditing(true)}
      disabled={readOnly}
      sx={{
        width: '120px',
        '& .MuiOutlinedInput-root': {
          backgroundColor: readOnly
            ? 'grey.100'
            : isEditing
            ? 'warning.50'
            : 'inherit',
        },
      }}
      inputProps={{
        style: { textAlign: 'right' },
        readOnly: readOnly,
      }}
    />
  );
};

EditableField.propTypes = {
  value: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  isInteger: PropTypes.bool,
  readOnly: PropTypes.bool,
};

function PrepareGold({ title, subtitle }) {
  const dispatch = useDispatch();
  const material = useSelector((state) => state.material);
  const daySumList = useSelector((state) => state.daySummaryList);
  // const daysumlist = useSelector((state) => state.daysumlist);
  const me = useSelector((state) => state.me);
  // console.log('daysumlist', daysumlist.rows);

  const canEdit = me?.userData?.department?.access?.OTHER?.prepareGold === true;

  const [searchTerm, setSearchTerm] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [editedRows, setEditedRows] = useState({});
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const allStocks = daySumList?.rows?.map((d) => d.stock).flat() || [];

  // ✅ ถ้ามีข้อมูล allStocks ให้จัดกลุ่มตามคลัง
  const groupedAllStocks = useMemo(() => {
    if (!Array.isArray(allStocks) || allStocks.length === 0) return {};

    // 1️⃣ Group by place
    const grouped = _.groupBy(allStocks, (item) =>
      typeof item.place === 'object'
        ? item.place || 'ไม่ระบุคลัง'
        : item.place || 'ไม่ระบุคลัง',
    );

    // 2️⃣ Sort items within each group (96.5% first)
    Object.keys(grouped).forEach((place) => {
      grouped[place].sort((a, b) => {
        const aType = a?.material_type || '';
        const bType = b?.material_type || '';
        if (aType.includes('96.5%') && !bType.includes('96.5%')) return -1;
        if (!aType.includes('96.5%') && bType.includes('96.5%')) return 1;
        return aType.localeCompare(bType, 'th');
      });
    });

    // 3️⃣ Sort warehouses (ให้ “คลังรูปพรรณใหม่” ขึ้นก่อน)
    const sortedPlaces = Object.keys(grouped).sort((a, b) => {
      if (a === 'คลังรูปพรรณใหม่') return -1;
      if (b === 'คลังรูปพรรณใหม่') return 1;
      return a.localeCompare(b, 'th');
    });

    // 4️⃣ Return grouped object
    return sortedPlaces.reduce((acc, place) => {
      acc[place] = grouped[place];
      return acc;
    }, {});
  }, [allStocks]);

  // Effects
  useEffect(() => {
    dispatch(actions.materialAll({ name, page, size }));
  }, [name, page, size, dispatch]);

  useEffect(() => {
    dispatch(actions.daySummaryListAll({ name, page, size }));
  }, [name, page, size, dispatch]);
  // useEffect(() => {
  //   dispatch(actions.daySummaryListAll({ name, page, size }));
  // }, [name, page, size, dispatch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setName(searchTerm);
      setPage(1);
    }, 700);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Group data by warehouse
  const groupedData = useMemo(() => {
    if (!material?.rows?.length) return {};

    const grouped = _.groupBy(material.rows, (item) =>
      typeof item.place === 'object'
        ? item.place?.name || 'ไม่ระบุคลัง'
        : item.place || 'ไม่ระบุคลัง',
    );

    // Sort items within each group (96.5% first)
    Object.keys(grouped).forEach((place) => {
      grouped[place].sort((a, b) => {
        const aType = a?.material_type?.name || '';
        const bType = b?.material_type?.name || '';
        if (aType.includes('96.5%') && !bType.includes('96.5%')) return -1;
        if (!aType.includes('96.5%') && bType.includes('96.5%')) return 1;
        return aType.localeCompare(bType, 'th');
      });
    });

    // Sort warehouses
    const sortedPlaces = Object.keys(grouped).sort((a, b) => {
      if (a === 'คลังรูปพรรณใหม่') return -1;
      if (b === 'คลังรูปพรรณใหม่') return 1;
      return a.localeCompare(b, 'th');
    });

    return sortedPlaces.reduce((acc, place) => {
      acc[place] = grouped[place];
      return acc;
    }, {});
  }, [material?.rows]);

  // Handlers
  const handleFieldSave = (id, field, value) => {
    if (!canEdit) {
      setSnackbar({
        open: true,
        message: 'คุณไม่มีสิทธิ์ในการแก้ไขข้อมูล',
        severity: 'error',
      });
      return;
    }
    setEditedRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const handleSaveAll = async () => {
    if (!canEdit)
      return showSnackbar('คุณไม่มีสิทธิ์ในการบันทึกข้อมูล', 'error');

    try {
      // ✅ 2. เตรียม payload สำหรับ summary
      const payload = {
        date: new Date(new Date().setDate(new Date().getDate() - 1)),
        stock: material?.rows?.map((row) => {
          const edited = editedRows[row?.id] || {};

          // const hasChanges = !_.isEmpty(editedRows[row.id]);
          const isGoldBar =
            row?.isbar || ['ทองคำแท่ง', 'ทองแท่ง'].includes(row?.name);

          const isOtherPlace = row?.place?.name === 'คลังอื่นๆ';

          let calculatedValue;
          if (isOtherPlace) {
            calculatedValue = (edited?.cost || 0) / (edited?.weight || 1) || 0;
          } else if (isGoldBar) {
            calculatedValue =
              ((edited?.cost || 0) / (edited?.weight || 1)) * 15.244 || 0;
            0;
          } else {
            (calculatedValue =
              ((edited?.cost || 0) / (edited?.weight || 1)) * 15.16) || 0;
          }
          return {
            material_id: row?.id,
            name: row?.name,
            place: row?.place?.name,
            material_type: row?.material_type?._id,
            inventory: (edited?.inventory ?? row?.inventory) || 0,
            cost: (edited?.cost ?? row?.cost) || 0,
            weight: (edited?.weight ?? row?.weight) || 0,
            avarage_cost: calculatedValue,
          };
        }),
      };

      //  ✅ 3. เรียกสร้าง day summary
      const response = await dispatch(actions.daySummaryCreate(payload));

      if (response?.success) {
        showSnackbar('บันทึกข้อมูลสำเร็จแล้ว');

        // ✅ เพิ่ม delay เล็กน้อยให้ backend save เสร็จจริง
        await new Promise((r) => setTimeout(r, 800));

        // ✅ รีโหลดข้อมูลหลังบันทึกสำเร็จ
        await dispatch(actions.materialAll({ name, page, size }));

        // ✅ รีเซ็ตสถานะฟอร์ม
        setEditedRows({});
        setIsFormDisabled(true);
        localStorage.setItem('isFormDisabled', 'true');
      } else {
        showSnackbar('เกิดข้อผิดพลาดในการสร้างข้อมูลสรุป', 'error');
        return;
      }
    } catch (err) {
      console.error('Error saving:', err);
      showSnackbar('เกิดข้อผิดพลาดในการบันทึกข้อมูล', 'error');
    }
  };

  const handleCancelAll = () => {
    if (!canEdit) return;
    setEditedRows({});
    setSnackbar({
      open: true,
      message: 'ยกเลิกการแก้ไขแล้ว',
      severity: 'info',
    });
  };

  const getCurrentValue = (row, field) => {
    const edited = editedRows[row.id] || {};
    return edited[field] !== undefined ? edited[field] : row[field];
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  // Render group
  const renderGroup = (placeName, rows) => {
    const editedCount = rows.filter((row) => editedRows[row.id]).length;

    return (
      <Box key={placeName} sx={{ mb: 4 }}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarehouseIcon color="primary" />
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {placeName}
                </Typography>
                <Chip
                  label={`${rows.length} รายการ`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                {editedCount > 0 && canEdit && (
                  <Chip
                    label={`แก้ไข ${editedCount}`}
                    color="warning"
                    size="small"
                  />
                )}
                {!canEdit && (
                  <Chip
                    label="ดูได้อย่างเดียว"
                    color="default"
                    size="small"
                    icon={<LockIcon />}
                  />
                )}
              </Box>
            }
            sx={{ pb: 1 }}
          />
        </Card>

        <Paper elevation={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    'ลำดับ',
                    'สินค้า',
                    'ประเภทสินค้า',
                    placeName === 'คลังอื่นๆ' ? 'จำนวน' : 'น้ำหนัก (g)',
                    placeName !== 'คลังอื่นๆ' ? 'จำนวน' : '',
                    'มูลค่า (บาท)',
                    placeName === 'คลังอื่นๆ'
                      ? 'มูลค่าเฉลี่ย'
                      : 'ค่าเฉลี่ยต่อบาท',
                  ]?.map((header, index) => (
                    <TableCell
                      key={header}
                      sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}
                      align={index > 2 ? 'center' : 'left'}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.map((row, index) => {
                  const hasChanges = !_.isEmpty(editedRows[row.id]);
                  const isGoldBar =
                    row?.isbar || ['ทองคำแท่ง', 'ทองแท่ง'].includes(row?.name);
                  const isOtherPlace = row?.place?.name === 'คลังอื่นๆ';

                  let calculatedValue;
                  if (isOtherPlace) {
                    calculatedValue = row.cost / row.weight;
                  } else if (isGoldBar) {
                    calculatedValue = (row.cost / row.weight) * 15.244;
                  } else {
                    calculatedValue = (row.cost / row.weight) * 15.16;
                  }
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor:
                          hasChanges && canEdit ? 'warning.50' : 'inherit',
                        '&:hover': { backgroundColor: 'grey.50' },
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row?.material_type?.name || '-'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <EditableField
                          value={getCurrentValue(row, 'weight')}
                          onSave={(value) =>
                            handleFieldSave(row.id, 'weight', value)
                          }
                          readOnly={!canEdit || isFormDisabled}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {placeName === 'คลังอื่นๆ' ? (
                          <></>
                        ) : (
                          <EditableField
                            value={getCurrentValue(row, 'inventory')}
                            onSave={(value) =>
                              handleFieldSave(row.id, 'inventory', value)
                            }
                            isInteger
                            readOnly={!canEdit || isFormDisabled}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <EditableField
                          value={getCurrentValue(row, 'cost')}
                          onSave={(value) =>
                            handleFieldSave(row.id, 'cost', value)
                          }
                          readOnly={!canEdit || isFormDisabled}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {calculatedValue >= 0
                          ? formatNumber(calculatedValue)
                          : '0.00'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };
  const renderDataFirst = () => {
    if (!Array.isArray(allStocks) || allStocks.length === 0) return null;

    const groupNames = Object.keys(groupedAllStocks);

    return (
      <>
        {groupNames.map((placeName) => (
          <Box key={placeName} sx={{ mb: 4 }}>
            <Card sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WarehouseIcon color="success" />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      📦 {placeName}
                    </Typography>
                    <Chip
                      label={`${groupedAllStocks[placeName].length} รายการ`}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                }
                sx={{ pb: 1 }}
              />
            </Card>

            <Paper elevation={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[
                        'ลำดับ',
                        'สินค้า',
                        'ประเภทสินค้า',
                        placeName === 'คลังอื่นๆ' ? 'จำนวน' : 'น้ำหนัก (g)',
                        placeName !== 'คลังอื่นๆ' ? 'จำนวน' : '',
                        'มูลค่า (บาท)',
                        'ค่าเฉลี่ยต่อบาท',
                      ].map((header, index) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: 'grey.100',
                          }}
                          align={index > 2 ? 'center' : 'left'}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {groupedAllStocks[placeName].map((item, index) => (
                      <TableRow key={item._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.material_type || '-'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {formatNumber(item.weight || 0)}
                        </TableCell>
                        <TableCell align="center">
                          {placeName === 'คลังอื่นๆ' ? (
                            <></>
                          ) : (
                            formatNumber(item.inventory || 0, 0)
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {formatNumber(item.cost || 0)}
                        </TableCell>
                        <TableCell align="center">
                          {formatNumber(item.avarage_cost || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        ))}
      </>
    );
  };

  if (material?.isLoading) return <Loading />;
  if (material?.message) return <Error message={material.message} />;

  const groupNames = Object.keys(groupedData);
  const hasEdits = !_.isEmpty(editedRows);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <ViewTitle title={title} subtitle={subtitle} />

      {!canEdit && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
          คุณอยู่ในโหมดดูข้อมูลเท่านั้น
          ไม่สามารถแก้ไขข้อมูลได้เนื่องจากไม่มีสิทธิ์การเข้าถึง
        </Alert>
      )}

      {/* Search */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon color="primary" />
              <Typography variant="h6">ค้นหาและกรอง</Typography>
              {!canEdit && <LockIcon color="disabled" fontSize="small" />}
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                label="ค้นหาสินค้า"
                placeholder="ป้อนชื่อสินค้าที่ต้องการค้นหา..."
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                ผลการค้นหา:{' '}
                <strong>{formatNumber(material?.total || 0, 0)}</strong> รายการ
                {!canEdit && (
                  <Chip
                    label="โหมดอ่านอย่างเดียว"
                    size="small"
                    color="default"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {hasEdits && (
            <Chip
              label={`${Object.keys(editedRows).length} รายการที่แก้ไข`}
              color="info"
              variant="outlined"
            />
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!hasEdits || allStocks.length > 0} // ⬅️ ปิดปุ่มถ้ามี allStocks
            onClick={handleSaveAll}
            color="success"
          >
            บันทึกทั้งหมด
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={!hasEdits}
            onClick={handleCancelAll}
          >
            ยกเลิกทั้งหมด
          </Button>
        </Box>
      )}

      {/* Content */}
      {groupNames.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              ไม่พบข้อมูลสินค้า
            </Typography>
            <Typography variant="body2" color="text.secondary">
              กรุณาลองค้นหาด้วยคำค้นอื่น
            </Typography>
          </CardContent>
        </Card>
      ) : allStocks.length > 0 ? (
        renderDataFirst()
      ) : (
        groupNames.map((placeName) =>
          renderGroup(placeName, groupedData[placeName]),
        )
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

PrepareGold.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

PrepareGold.defaultProps = {
  title: 'การเตรียมทองคำ',
  subtitle: 'แก้ไขข้อมูลทองคำและวัตถุดิบหลายรายการ',
};

export default PrepareGold;
